import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useI18n } from "../context/I18nContext";
import { useAuth } from "../context/AuthContext";
import { SecuritySettings } from "./SecuritySettings";
import { Button } from "./ui/button";
import { Translate, SignOut, House, Gauge, ShieldCheck, LockKey } from "@phosphor-icons/react";

export const Header = () => {
  const { t, lang, toggle } = useI18n();
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [secOpen, setSecOpen] = useState(false);

  const goDash = () => navigate(user?.role === "client" ? "/dashboard" : "/admin");

  return (
    <header className="sticky top-0 z-50 bg-white/85 backdrop-blur-xl border-b border-gray-200">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
        <Link to="/" data-testid="brand-logo" className="flex items-center gap-2.5 group min-w-0 shrink">
          <img src="/logo.png" alt="logo" className="h-9 w-9 object-contain shrink-0" />
          <div className="leading-none min-w-0">
            <div className="font-display font-extrabold text-emerald-900 text-base sm:text-lg tracking-tight truncate">
              {t("brand")}
            </div>
            <div className="text-[10px] uppercase tracking-widest text-emerald-600 font-semibold hidden sm:block">
              {t("tagline")}
            </div>
          </div>
        </Link>

        <div className="flex items-center gap-2 sm:gap-3">
          <Link
            to="/verificar"
            data-testid="header-verify-link"
            className="hidden sm:flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-600 px-2.5 py-1.5 rounded-md hover:bg-emerald-50 transition-colors"
          >
            <ShieldCheck size={18} weight="bold" />
            {t("nav_verify")}
          </Link>
          <button
            data-testid="lang-toggle"
            onClick={toggle}
            className="flex items-center gap-1.5 text-sm font-semibold text-emerald-800 hover:text-emerald-600 px-2.5 py-1.5 rounded-md hover:bg-emerald-50 transition-colors"
          >
            <Translate size={18} weight="bold" />
            {lang === "es" ? "EN" : "ES"}
          </button>

          {user ? (
            <>
              <Button
                data-testid="header-dashboard-btn"
                onClick={goDash}
                variant="ghost"
                className="text-emerald-800 hover:bg-emerald-50 gap-1.5"
              >
                {user.role !== "client" ? <Gauge size={18} weight="bold" /> : <House size={18} weight="bold" />}
                <span className="hidden sm:inline">{user.role !== "client" ? t("admin_panel") : t("dashboard")}</span>
              </Button>
              <Button
                data-testid="header-security-btn"
                onClick={() => setSecOpen(true)}
                variant="ghost"
                className="text-emerald-800 hover:bg-emerald-50 gap-1.5"
              >
                <LockKey size={18} weight="bold" />
                <span className="hidden sm:inline">{t("security")}</span>
              </Button>
              <Button
                data-testid="header-logout-btn"
                onClick={() => { logout(); navigate("/"); }}
                className="bg-emerald-800 hover:bg-emerald-900 gap-1.5"
              >
                <SignOut size={18} weight="bold" />
                <span className="hidden sm:inline">{t("logout")}</span>
              </Button>
            </>
          ) : (
            <>
              <Button
                data-testid="header-login-btn"
                onClick={() => navigate("/login")}
                variant="ghost"
                className="text-emerald-800 hover:bg-emerald-50"
              >
                {t("login")}
              </Button>
              <Button
                data-testid="header-register-btn"
                onClick={() => navigate("/register")}
                className="bg-emerald-800 hover:bg-emerald-900 shadow-sm transition-transform hover:-translate-y-0.5"
              >
                {t("register")}
              </Button>
            </>
          )}
        </div>
      </div>
      {user && <SecuritySettings open={secOpen} onClose={() => setSecOpen(false)} />}
    </header>
  );
};
